import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import type { MessageKey } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import { useToast } from '@/ui'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Readout,
  Row,
  SheetActions,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { C, typeKey } from '@/features/gate/v1/style'
import { shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { ReceiveShipmentPayload, ScanRefusal } from '../commands'
import type { StoreState, TripView } from '../projection'
import { classifyScan, reconcileManifest, type ReceiveScan } from '../rules'
import { useDraftState, useNow } from '../useStore'
import {
  DoneScreen,
  Hint,
  IconBox,
  RefusedCard,
  RefusedScreen,
  Saved,
  StoreHeader,
  Tally,
  VehicleRow,
  WithStore,
  agoText,
  holdNote,
  supervisorPhone,
} from './bits'
import { SIcon } from './icons'
import {
  arrivingTrips,
  last4,
  mergeScans,
  takeHeld,
  tripBags,
  tripWhich,
  type HeldGroup,
} from './logic'
import { TagScanner } from './TagScanner'

/** Scans of vehicles the list did not know yet, kept per godown until their trip arrives. */
export const heldDraftId = (siteId: string) => `store.v1.held:${siteId}`

const WHY: ReadonlySet<string> = new Set([
  'NOT_AT_ORIGIN',
  'ALREADY_LOADED',
  'NOT_STORED',
  'TAG_VOID',
  'TAG_RETIRED',
  'TAG_UNKNOWN',
  'TAG_WRONG_KIND',
])

/** The board's one-line reason for a refused tag. */
export const whyText = (reason: string) =>
  tr((WHY.has(reason) ? `store.v1.why.${reason}` : 'store.v1.why.OTHER') as MessageKey)

/** V1-F5-01 — माल उतारें: pick the vehicle, scan every bag off, receive. */
export function V1Receive() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tripId = params.get('trip')
  const home = () => navigate('/m/store')
  // Kept here, not in ReceiveTrip: once received, the trip leaves the arriving list (the pending
  // receipt, then the next download), which would unmount the trip screen with its done screen.
  const [done, setDone] = useState<Received | null>(null)
  return (
    <WithStore onBack={home}>
      {(state) => {
        if (done) return <ReceivedScreen done={done} state={state} onHome={home} />
        const trip = tripId ? state.trips.get(tripId) : undefined
        return trip ? (
          <ReceiveTrip
            key={trip.shipment_id}
            state={state}
            trip={trip}
            onHome={home}
            onDone={setDone}
          />
        ) : (
          <PickVehicle state={state} onPick={(id) => setParams({ trip: id })} onHome={home} />
        )
      }}
    </WithStore>
  )
}

/** What a receipt took in, for the green माल लिया screen. */
interface Received {
  bags: number
  lots: number
  missing: number
}

function ReceivedScreen({
  done,
  state,
  onHome,
}: {
  done: Received
  state: StoreState
  onHome: () => void
}) {
  return (
    <DoneScreen
      title={tr('store.v1.received.title')}
      lines={tr('store.v1.received.bags', { count: done.bags }).replaceAll('**', '')}
      chips={[
        {
          icon: 'godown',
          text: tr('store.v1.received.lots', {
            count: done.lots,
            place: state.cache.site_name ?? '',
          }),
        },
        ...(done.missing > 0
          ? [
              {
                icon: 'bang' as const,
                text: tr('store.v1.received.missing', { count: done.missing }),
              },
            ]
          : []),
      ]}
      onHome={onHome}
    />
  )
}

function useHeld(siteId: string, actorId: string) {
  return useDraftState<{ groups: HeldGroup[] }>(
    heldDraftId(siteId),
    'store.v1.held',
    () => ({ groups: [] }),
    actorId,
  )
}

// ---------------------------------------------------------------------------------------------
// Which vehicle came?
// ---------------------------------------------------------------------------------------------

function PickVehicle({
  state,
  onPick,
  onHome,
}: {
  state: StoreState
  onPick: (tripId: string) => void
  onHome: () => void
}) {
  const actor = useActor()
  const [held] = useHeld(state.siteId, actor.user_id)
  const [unknown, setUnknown] = useState(false)
  const [digits, setDigits] = useState<string | null>(null)
  const now = useNow()
  const trips = arrivingTrips(state)

  if (digits) return <HeldScan state={state} digits={digits} onDone={onHome} />
  if (unknown) return <UnknownVehicle onBack={() => setUnknown(false)} onDigits={setDigits} />

  return (
    <V1Screen
      header={
        <StoreHeader onBack={onHome} icon="truck" title={tr('store.v1.pick.title')} step={1} />
      }
      footer={
        <Footer>
          <Row>
            <Btn icon="search" onClick={() => setUnknown(true)}>
              {tr('store.v1.pick.unknown')}
            </Btn>
          </Row>
        </Footer>
      }
    >
      {trips.length === 0 && <Strip icon="truck">{tr('store.v1.home.none')}</Strip>}
      {trips.map((t, i) => {
        const matched = held.groups
          .filter((g) => g.digits === last4(t.vehicle_no))
          .reduce((n, g) => n + g.scans.length, 0)
        return (
          <VehicleRow
            key={t.shipment_id}
            first={i === 0}
            chevron
            vehicle={t.vehicle_no ?? t.code}
            line={tr('store.v1.tripLine', { village: t.from_name ?? '', count: tripBags(t) })}
            extra={
              matched > 0
                ? `${tr('store.v1.pick.matched', { count: matched })} · ${tripWhich(t, now)}`
                : `${tripWhich(t, now)}${t.dispatched_at ? ` · ${agoText(t.dispatched_at, now)}` : ''}`
            }
            onClick={() => onPick(t.shipment_id)}
          />
        )
      })}
    </V1Screen>
  )
}

/** गाड़ी सूची में नहीं: the last four digits of the plate, then unload and match later. */
function UnknownVehicle({
  onBack,
  onDigits,
}: {
  onBack: () => void
  onDigits: (d: string) => void
}) {
  const [v, setV] = useState('')
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 0, max: 4 })), [])
  return (
    <V1Screen
      header={
        <StoreHeader onBack={onBack} icon="truck" title={tr('store.v1.pick.unknown')} step={1} />
      }
      footer={
        <Footer>
          {v.length === 4 ? (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={() => onDigits(v)} />
          ) : (
            <WaitCta>{tr('store.v1.unknown.wait')}</WaitCta>
          )}
        </Footer>
      }
    >
      <Hint icon="hourglass" tone="warn" text={tr('store.v1.unknown.later')} />
      <Readout label={tr('store.v1.unknown.digits')} value={v} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Screen>
  )
}

/** Bags off a vehicle the list does not know: kept on the phone, matched when its trip arrives. */
function HeldScan({
  state,
  digits,
  onDone,
}: {
  state: StoreState
  digits: string
  onDone: () => void
}) {
  const actor = useActor()
  const [held, update, loaded] = useHeld(state.siteId, actor.user_id)
  const [saved, setSaved] = useState<string | null>(null)
  const scans = held.groups.find((g) => g.digits === digits)?.scans ?? []

  const onCode = (code: string) => {
    const k = classifyScan(state, code)
    if (k.kind === 'LOCATION' || k.kind === 'SEAL') return whyText('TAG_WRONG_KIND')
    if (k.kind === 'DEAD') return whyText(k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED')
    if (scans.some((s) => s.qr_payload === code)) return null
    const scan: ReceiveScan = { qr_payload: code, scanned_at: new Date().toISOString() }
    update((d) => {
      const has = d.groups.some((g) => g.digits === digits)
      return {
        groups: has
          ? d.groups.map((g) =>
              g.digits === digits ? { ...g, scans: mergeScans(g.scans, [scan]) } : g,
            )
          : [...d.groups, { digits, scans: [scan] }],
      }
    })
    setSaved(code)
    return null
  }

  if (!loaded) return <V1Screen />
  return (
    <V1Screen
      header={
        <StoreHeader
          onBack={onDone}
          icon="truck"
          title={tr('store.v1.held.vehicle', { digits })}
          step={2}
        />
      }
      footer={
        <Footer>
          <Row>
            <Btn icon="clock" onClick={onDone}>
              {tr('store.v1.held.later')}
            </Btn>
          </Row>
          {scans.length > 0 ? (
            <Cta label={tr('store.v1.held.allOff')} icon="ok" onClick={onDone} />
          ) : (
            <WaitCta>{tr('store.v1.scanOff.first')}</WaitCta>
          )}
        </Footer>
      }
    >
      {saved && <Saved text={tr('store.v1.held.saved', { tag: shortCode(saved) })} />}
      <ItemRow
        lead={<IconBox icon="truck" size={44} iconSize={26} />}
        title={tr('store.v1.held.vehicle', { digits })}
        right={<Tally value={String(scans.length)} label={tr('store.v1.scanOff.off')} />}
      />
      <TagScanner
        hint={tr(scans.length ? 'store.v1.scanOn.hold' : 'store.v1.scanOff.first')}
        payloads={[...state.bagByPayload.keys()]}
        onCode={onCode}
      />
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// Scan off and receive
// ---------------------------------------------------------------------------------------------

interface ReceiveDraft {
  scans: ReceiveScan[]
  /** Held scans of this vehicle were taken in. */
  merged: boolean
}

function ReceiveTrip({
  state,
  trip,
  onHome,
  onDone,
}: {
  state: StoreState
  trip: TripView
  onHome: () => void
  onDone: (done: Received) => void
}) {
  const actor = useActor()
  const toast = useToast()
  const [draft, update, loaded, clear] = useDraftState<ReceiveDraft>(
    `store.v1.receive:${trip.shipment_id}`,
    'store.v1.receive',
    () => ({ scans: [], merged: false }),
    actor.user_id,
  )
  const [held, updateHeld, heldLoaded] = useHeld(state.siteId, actor.user_id)
  const [saved, setSaved] = useState<string | null>(null)
  const [refused, setRefused] = useState<{ tag: string; reason: ScanRefusal } | null>(null)
  const [short, setShort] = useState(false)
  const [saving, setSaving] = useState(false)

  // Scans saved before the list knew this vehicle (गाड़ी सूची में नहीं) join it once.
  const mergedOnce = useRef(false)
  useEffect(() => {
    if (!loaded || !heldLoaded || draft.merged || mergedOnce.current) return
    mergedOnce.current = true
    const { scans, rest } = takeHeld(held.groups, trip)
    update((d) => ({ scans: mergeScans(d.scans, scans), merged: true }))
    if (scans.length) updateHeld(() => ({ groups: rest }))
  }, [loaded, heldLoaded, draft.merged, held.groups, trip, update, updateHeld])

  const rec = useMemo(() => reconcileManifest(state, trip, draft.scans), [state, trip, draft.scans])
  const payloads = useMemo(
    () => [...trip.items.map((i) => i.qr_payload), ...state.bagByPayload.keys()],
    [trip.items, state.bagByPayload],
  )

  const onCode = (code: string) => {
    if (draft.scans.some((s) => s.qr_payload.toUpperCase() === code)) return null
    const scan = { qr_payload: code, scanned_at: new Date().toISOString() }
    update((d) => ({ ...d, scans: mergeScans(d.scans, [scan]) }))
    if (trip.items.some((i) => i.qr_payload.toUpperCase() === code)) {
      setSaved(code)
      return null
    }
    // Not on this vehicle's list: a good bag tag is taken (the office sees the extra); anything
    // else is kept aside and reported with the receipt.
    const k = classifyScan(state, code)
    if (k.kind === 'BAG') {
      setSaved(code)
      return null
    }
    setRefused({
      tag: code,
      reason:
        k.kind === 'DEAD'
          ? k.status === 'VOID'
            ? 'TAG_VOID'
            : 'TAG_RETIRED'
          : k.kind === 'UNKNOWN'
            ? 'TAG_UNKNOWN'
            : 'TAG_WRONG_KIND',
    })
    return null
  }

  const openBags = rec.open.filter((i) => i.container_id).length

  const receive = async () => {
    setSaving(true)
    try {
      const payload: ReceiveShipmentPayload = {
        shipment_id: trip.shipment_id,
        received_at: new Date().toISOString(),
        unloads: rec.unloaded.map((u) => ({ item_id: u.item.item_id, unloaded_at: u.scanned_at })),
        extras: rec.extras.map((x) => ({
          item_id: newId(),
          qr_payload: x.bag.qr_payload,
          unloaded_at: x.scanned_at,
        })),
        // V1: no check weighing at the godown; the server raises a missing re-weigh for the office.
        check_weights: [],
        // V1 asks no reason: one "Yes, receive" after looking again.
        missing: rec.open.map((i) => ({ item_id: i.item_id })),
        refused_scans: rec.refused.map((r) => ({ ...r, photo_id: null })),
      }
      const parents = trip.local
        ? [
            ...new Set(
              [
                ...rec.unloaded.map((u) => u.item.container_id),
                ...rec.extras.map((x) => x.bag.container_id),
              ]
                .map((id) => (id ? state.bags.get(id)?.pending_command_id : null))
                .filter((x): x is string => !!x),
            ),
          ]
        : []
      await enqueueCommand({
        type: 'receive_shipment',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      const lots = new Set(
        [
          ...rec.unloaded.map((u) => u.item.unit_id),
          ...rec.extras.map((x) => x.bag.unit_id),
        ].filter(Boolean),
      ).size
      // The receipt is queued: show माल लिया even if clearing the draft fails, so it is not sent twice.
      onDone({ bags: rec.bagsScanned, lots, missing: openBags })
      await clear().catch(() => undefined)
    } catch (e) {
      setShort(false)
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded || !heldLoaded) return <V1Screen />
  if (refused)
    return (
      <RefusedScreen
        icon="truck"
        title={tr('store.v1.problem.title')}
        step={1}
        phone={supervisorPhone(state)}
        onBack={() => setRefused(null)}
        onOk={() => setRefused(null)}
        card={
          <RefusedCard
            title={tr('store.v1.refused.receive', { tag: shortCode(refused.tag) })}
            why={whyText(refused.reason)}
            steps={[tr('store.v1.refused.receiveStep1'), tr('store.v1.refused.receiveStep2')]}
          />
        }
      />
    )

  const lots = [...rec.byLot.entries()]
  return (
    <V1Screen
      header={
        <StoreHeader onBack={onHome} icon="truck" title={tr('store.v1.scanOff.title')} step={1} />
      }
      footer={
        <Footer>
          {rec.bagsScanned > 0 ? (
            <Cta
              label={tr('store.v1.scanOff.receive')}
              icon="ok"
              busy={saving}
              onClick={() => (openBags > 0 ? setShort(true) : void receive())}
            />
          ) : (
            <WaitCta>{tr('store.v1.scanOff.first')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        short ? (
          <V1Sheet
            title={tr('store.v1.short.title', { count: openBags })}
            onClose={() => setShort(false)}
            top={220}
            actions={
              <SheetActions>
                <Btn icon="back" onClick={() => setShort(false)}>
                  {tr('store.v1.short.back')}
                </Btn>
                <Cta
                  label={tr('store.v1.short.yes')}
                  busy={saving}
                  onClick={() => void receive()}
                />
              </SheetActions>
            }
          >
            <b style={{ fontSize: 20 }}>
              {tr('store.v1.short.off', { n: rec.bagsScanned, m: rec.bagsExpected })}
            </b>
            <Hint icon="eye" text={tr('store.v1.short.look')} />
            <Hint icon="bang" tone="warn" text={tr('store.v1.short.warn', { count: openBags })} />
          </V1Sheet>
        ) : undefined
      }
    >
      {saved && <Saved text={tr('store.v1.tag', { tag: shortCode(saved) })} />}
      <VehicleRow
        vehicle={trip.vehicle_no ?? trip.code}
        line={tr('store.v1.tripLine', { village: trip.from_name ?? '', count: tripBags(trip) })}
        extra={tripWhich(trip)}
        right={
          <Tally
            value={`${rec.bagsScanned}/${rec.bagsExpected}`}
            label={tr('store.v1.scanOff.off')}
          />
        }
      />
      <TagScanner
        hint={tr(rec.bagsScanned ? 'store.v1.scanOn.hold' : 'store.v1.scanOff.first')}
        payloads={payloads}
        onCode={onCode}
        paused={short}
      />
      {lots.map(([unitId, v]) => {
        const lot = state.lots.get(unitId)
        const red = lot?.stream === 'CONV'
        return (
          <ItemRow
            key={unitId}
            padding="6px 10px"
            lead={
              <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
                <SIcon name={red ? 'redtie' : 'sack'} size={26} />
              </span>
            }
            title={tr('store.v1.scanOff.band', {
              grade: tr(red ? 'store.v1.band.red' : 'store.v1.band.good'),
              lot: lot ? shortLot(lot.code) : '—',
              n: v.scanned,
              m: v.expected,
            })}
            line={holdNote(lot)}
            right={v.scanned === v.expected ? <SIcon name="ok" size={22} sw={3} /> : undefined}
          />
        )
      })}
    </V1Screen>
  )
}
